import React, { useState } from "react";
import PropTypes from "prop-types";
import Request from "../../utils/Request";
import Config from "../../utils/config";
import { toast } from "react-toastify";
import { Modal, Box, Typography, Button, CircularProgress } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";

import ChevronRightIcon from "@mui/icons-material/ChevronRight";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 420,
  maxWidth: "95%",
  bgcolor: "background.paper",
  borderRadius: 2,
  boxShadow: 24,
  p: 3,
};

export default function CsvUploadModal({ isOpen, onClose, onUploaded, uploadEndpoint }) {
  const [csvFile, setCsvFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const endpoint = uploadEndpoint || Config.apis?.user?.UPLOAD_MCC_CSV;

  const onFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      setCsvFile(null);
      return;
    }
    if (!file.name.toLowerCase().endsWith(".csv")) {
      toast.error("Please select a valid CSV file");
      setCsvFile(null);
      return;
    }
    setCsvFile(file);
  };

  const extractMessage = (payload) => {
    if (!payload) return null;
    if (typeof payload === "string") return payload;
    if (payload.msg) return payload.msg;
    if (payload.message) return payload.message;
    if (payload.error) return payload.error;
    if (payload.errors) {
      if (Array.isArray(payload.errors) && payload.errors.length) {
        const first = payload.errors[0];
        return typeof first === "string" ? first : first.message || JSON.stringify(first);
      }
      if (typeof payload.errors === "object") {
        const key = Object.keys(payload.errors)[0];
        const v = payload.errors[key];
        return Array.isArray(v) ? v[0] : v;
      }
    }
    if (payload.data && (payload.data.msg || payload.data.message)) {
      return payload.data.msg || payload.data.message;
    }
    return null;
  };

  const upload = async () => {
    if (!csvFile) {
      toast.error("Please select a CSV file to upload");
      return;
    }
    if (!endpoint) {
      toast.error("Upload endpoint not configured");
      return;
    }

    setUploading(true);

    const formData = new FormData();
    formData.append("file", csvFile);

    const successHandler = (resData, status) => {
      setUploading(false);

      const msg = extractMessage(resData);
      const isApiSuccess =
        (typeof resData === "object" && (resData.code === "0000" || resData.status === 200)) ||
        (typeof status === "number" && status >= 200 && status < 300);

      if (isApiSuccess) {
        toast.success(msg || "CSV uploaded successfully");
        try {
          onUploaded?.(resData);
        } catch (err) {
          console.error("onUploaded callback error:", err);
        }
        onClose?.();
      }
    };

    const errorHandler = (err) => {
      setUploading(false);
      const respData = err?.response?.data;
      const respStatus = err?.response?.status;
      const msg =
        extractMessage(respData) ||
        (err && err.message) ||
        (respStatus ? `Upload failed (status ${respStatus})` : "CSV upload failed");

      toast.error(msg);
      console.debug("[CsvUploadModal] errorHandler executed:", msg);
    };

    const api = new Request(null, successHandler, errorHandler);

    try {
      await api.upload(endpoint, formData);
    } catch (e) {
      setUploading(false);
      console.debug("[CsvUploadModal] upload rejected (handled by Request.errorFn)");
    }
  };

  return (
    <Modal open={isOpen} onClose={onClose} aria-labelledby="upload-csv-title">
      <Box sx={modalStyle}>
        <Typography id="upload-csv-title" variant="h6">
          Upload CSV File
        </Typography>

        <input type="file" accept=".csv" onChange={onFileChange} style={{ marginTop: 8 }} />

        {csvFile && (
          <Typography sx={{ fontSize: 13, mt: 1 }}>
            Selected file: <strong>{csvFile.name}</strong>
          </Typography>
        )}

        {/* View Sample Button */}
        <Box sx={{ mt: 2, textAlign: "left" }}>
          <Button
            variant="text"
            onClick={() =>
              window.open("https://app-user-asset.s3.ap-south-1.amazonaws.com/sample-files/mcc.csv", "_blank")
            }
            sx={{
              textTransform: "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              gap: 1,
              color: "red",
              fontSize: 14,
              padding: 0,
              minWidth: 0,
            }}
          >
            <DownloadIcon fontSize="small" />
            Download Sample
            <ChevronRightIcon fontSize="small" />
          </Button>
        </Box>

        {/* Buttons */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 2 }}>
          <Button
            variant="outlined"
            onClick={onClose}
            disabled={uploading}
            sx={{
              color: "red",
              borderColor: "red",
              "&:hover": {
                borderColor: "red",
                backgroundColor: "rgba(255, 0, 0, 0.08)", // translucent red
              },
              "&.Mui-disabled": {
                color: "rgba(0,0,0,0.26)",
                borderColor: "rgba(0,0,0,0.12)",
              },
            }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={upload}
            disabled={uploading || !csvFile}
            sx={{
              backgroundColor: "red",
              "&:hover": { backgroundColor: "darkred" },
              minWidth: 110,
            }}
          >
            {uploading ? (
              <span style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <CircularProgress size={18} color="inherit" />
                Uploading...
              </span>
            ) : (
              "Upload"
            )}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}

CsvUploadModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onUploaded: PropTypes.func,
  uploadEndpoint: PropTypes.string,
};
